/**
 * ClickData
 * 
 * Responsible for parsing click platform and identifier from url params.
 */
import { ParsedUrlParams } from './urlparams';
import { Utms } from './utms';

type PlatformConfig = Record<string, Array<string>>;

export interface Result {
    platform: Array<string>;
    id: Array<string>;
}

export default class ClickData {

    /**
     * NOTE: 
     * 
     *  Platform can never been "None" for an empty result.
     * 
     *  This will break some lead assignment rules that rely on
     *  an empty SessionProfile query ([]) resembling failure for the given attribute.
     * 
    */ 
    static platforms: Record<string, PlatformConfig> = {
        'Google Ads': {
            // gbraid/wbraid are for tracking users who have opted out
            // on iOS 14.x
            'identifier': [
                'gclid',
                'gbraid',
                'wbraid',
                // new tracking click id for display and video inventory
                // see: https://support.google.com/campaignmanager/answer/9182069?hl=en
                'dclid',
            ],
        },
        'Bing Ads': {
            'identifier': [
                'msclkid',
            ],
        },
        'Facebook Ads': {
            'identifier': [
                'fbclid',
                'kcid', // "new" 2021-12 - use for facebook retargeting
            ],
        },
        'TikTok Ads': {
            'identifier': [
                'ttclid',
            ]
        },
        'LinkedIn Ads': {
            'identifier': [
                'li_fat_id',
            ]
        },
        'Pinterest Ads': {
            'identifier': [
                'epik',
            ]
        },
        'Snapchat Ads': {
            'identifier': [
                'ScCid',
            ]
        },
        'X Ads': {
            'identifier': [
                'twclid'
            ]
        },
        'Reddit Ads': {
            'identifier': [
                'rdt_cid'
            ]
        }
    };

    public static parse(params: ParsedUrlParams) : Result {
        // for each click platform - check the url params against the
        // identifiers
        for (const key in this.platforms) {
            const identifiers = this.platforms[key].identifier;
            for (let i = 0; i < identifiers.length; i++) {
                const identifier = identifiers[i];
                if (typeof(params[identifier]) !== 'undefined') {
                    const id = params[identifier];
                    return {
                        platform: [key],
                        id: [String(id)],
                    };
                }
            }
        }

        // source-medium based hints
        const utms = new Utms(params);
        const source = utms.getSource();
        const medium = utms.getMedium();
        if (source !== null && medium !== null) {
            const isPaidMedium = ['cpc', 'ppc'].indexOf(medium) !== -1;
            const isPaidSocialMedium = ['paid-social'].indexOf(medium) !== -1;
            if (source === 'google' && isPaidMedium) {
                return {
                    platform: ['Google Ads'],
                    id: [],
                };
            }
            if (source === 'bing' && isPaidMedium) {
                return {
                    platform: ['Bing Ads'],
                    id: [],
                };
            }
            if (source === 'facebook' && isPaidSocialMedium) {
                return {
                    platform: ['Facebook Ads'],
                    id: [],
                };
            }
        }

        // other non-click identifier hints for Gads
        const otherGAdsHints = [
            'gad_source',
            'gad_campaignid'
        ];
        for (let i = 0; i < otherGAdsHints.length; i ++) {
            const found = typeof(params[otherGAdsHints[i]]) !== 'undefined';
            if (found) {
                return {
                    platform: ['Google Ads'],
                    id: [],
                }
            }
        }

        // not (paid|display) = empty platform and id
        return {
            platform: [],
            id: [],
        };
    }
}
